import { MenuButton, MenuItemWithIcon } from "@msys/ui";
import {
  DataObject as DataObjectIcon,
  GetApp as DownloadIcon,
} from "@mui/icons-material";
import {
  CircularProgress,
  Divider,
  ListSubheader,
  useTheme,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useFeature } from "../../../../common/FeatureFlags.js";
import { useOpenFile } from "../../../commons/hooks/useOpenFile.js";
import {
  PdfDownloadArgs,
  useDownloadQuotePdf,
} from "../hooks/useDownloadQuotePdf.js";
import { useDownloadQuotePdfOptions } from "../hooks/useDownloadQuotePdfOptions.js";
import {
  QuoteDataCalculationType,
  useGetQuoteData,
} from "../hooks/useGetQuoteData.js";
import { QuoteDataInspectorModal } from "../QuoteDataInspectorModal.js";
import { DownloadQuoteAsPdfButton_QuoteFragment } from "./DownloadQuoteAsPdfIconButton.generated.js";

export const DownloadQuoteAsPdfIconButton = ({
  docId,
  projectId,
  doc,
}: {
  docId: string;
  projectId: string;
  doc: DownloadQuoteAsPdfButton_QuoteFragment;
}) => {
  const theme = useTheme();
  const { t } = useTranslate(["Quote"]);
  const [open, setOpen] = React.useState<boolean>(false);

  const { handleDownload, loadingPdf } = useDownloadQuotePdf({
    projectId,
    docId,
  });

  return (
    <MenuButton
      onToggle={setOpen}
      Icon={
        loadingPdf ? (
          <CircularProgress size="1.5rem" color={"primary"} />
        ) : (
          <DownloadIcon />
        )
      }
      tooltip={t("Download PDF", { ns: "Quote" })}
      menuProps={{
        slotProps: {
          paper: {
            style: {
              width: theme.layout.filters.popoverWidth.lg,
              minWidth: theme.layout.filters.popoverWidth.lg,
              maxWidth: theme.layout.filters.popoverWidth.lg,
            },
          },
        },
      }}
    >
      {open ? (
        <DownloadQuoteAsPdfMenuItems
          docId={docId}
          projectId={projectId}
          doc={doc}
          loadingPdf={loadingPdf}
          handleDownload={handleDownload}
        />
      ) : (
        <div />
      )}
    </MenuButton>
  );
};

function DownloadQuoteAsPdfMenuItems({
  docId,
  projectId,
  doc,
  loadingPdf,
  handleDownload,
}: {
  docId: string;
  projectId: string;
  doc: DownloadQuoteAsPdfButton_QuoteFragment;
  loadingPdf: string | null;
  handleDownload: (args: PdfDownloadArgs) => Promise<string | undefined>;
}) {
  const { t } = useTranslate(["Quote", "Global"]);
  const { openPdf } = useOpenFile();
  const { handleFetch: fetchQuoteData, loadingQuoteData } = useGetQuoteData({
    projectId,
    docId,
  });
  const [inspectorData, setInspectorData] = React.useState<unknown>(null);

  const getHandleInspect =
    (calculationType: QuoteDataCalculationType) =>
    async (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const quoteData = await fetchQuoteData(calculationType);
      if (quoteData !== null && quoteData !== undefined) {
        setInspectorData(quoteData);
      }
    };

  const getHandleClick =
    (args: PdfDownloadArgs) => async (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const url = await handleDownload(args);
      if (!url) {
        throw new Error("Not available at this moment, please try again later");
      }
      await openPdf(url, `Quote_${doc.id}`);
    };

  const {
    hasContractingTemplates,
    hasPdgGeneratorTemplates,
    hasSexyQuotePdfTemplates,
    hasSelectedCalculations,
    contractingPdfs,
    sexyQuotePdfTemplates,
    pdfGeneratorTemplates,
    loading,
  } = useDownloadQuotePdfOptions({ doc });

  const debug = useFeature("DebugOutput");

  return (
    <>
      {hasSelectedCalculations && (
        <MenuItemWithIcon
          icon={
            loadingPdf === "none-agreed" ? (
              <CircularProgress size="1.5rem" color={"primary"} />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={getHandleClick({
            templateType: "none",
            calculationType: "agreed",
          })}
          disabled={loadingPdf === "none-agreed"}
          sx={{ whiteSpace: "normal" }}
        >
          {`${t("Quote PDF", { ns: "Quote" })} (${t("agreed", { ns: "Quote" })})`}
        </MenuItemWithIcon>
      )}
      <MenuItemWithIcon
        icon={
          loadingPdf === "none-proposed" ? (
            <CircularProgress size={24} color={"primary"} />
          ) : (
            <DownloadIcon />
          )
        }
        onClick={getHandleClick({
          templateType: "none",
          calculationType: "proposed",
        })}
        disabled={loadingPdf === "none-proposed"}
        sx={{ whiteSpace: "normal" }}
      >
        {hasSelectedCalculations
          ? `${t("Quote PDF", { ns: "Quote" })} (${t("proposed", { ns: "Quote" })})`
          : t("Quote PDF", { ns: "Quote" })}
      </MenuItemWithIcon>
      {hasContractingTemplates && [
        <Divider key="divider-contracting" />,
        <ListSubheader
          key="header-contracting"
          sx={{ color: "grey.500", minHeight: "36px", lineHeight: "36px" }}
        >
          {t("Contracting PDF", { ns: "Quote" })}
        </ListSubheader>,
        ...contractingPdfs.flatMap(contractingPdf =>
          [
            hasSelectedCalculations && (
              <MenuItemWithIcon
                key={`${contractingPdf.id}-agreed`}
                icon={
                  loadingPdf === `contracting-agreed-${contractingPdf.id}` ? (
                    <CircularProgress size="1.5rem" color={"primary"} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={getHandleClick({
                  templateType: "contracting",
                  calculationType: "agreed",
                  contractingPdfId: contractingPdf.id,
                })}
                disabled={
                  loadingPdf === `contracting-agreed-${contractingPdf.id}`
                }
                sx={{ whiteSpace: "normal" }}
              >
                {`${contractingPdf.title} (${t("agreed", { ns: "Quote" })})`}
              </MenuItemWithIcon>
            ),
            <MenuItemWithIcon
              key={`${contractingPdf.id}-proposed`}
              icon={
                loadingPdf === `contracting-proposed-${contractingPdf.id}` ? (
                  <CircularProgress size={24} color={"primary"} />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={getHandleClick({
                templateType: "contracting",
                calculationType: "proposed",
                contractingPdfId: contractingPdf.id,
              })}
              disabled={
                loadingPdf === `contracting-proposed-${contractingPdf.id}`
              }
              sx={{ whiteSpace: "normal" }}
            >
              {hasSelectedCalculations
                ? `${contractingPdf.title} (${t("proposed", { ns: "Quote" })})`
                : contractingPdf.title}
            </MenuItemWithIcon>,
          ].filter(value => value)
        ),
      ]}
      {hasSexyQuotePdfTemplates && [
        <Divider key="divider-sexy" />,
        <ListSubheader
          key="header-sexy"
          sx={{ color: "grey.500", minHeight: "36px", lineHeight: "36px" }}
        >
          {t("Sexy PDF", { ns: "Quote" })}
        </ListSubheader>,
        ...sexyQuotePdfTemplates.map(pdfTemplate => (
          <MenuItemWithIcon
            key={`${pdfTemplate.pdfTemplateUrl}-proposed`}
            icon={
              loadingPdf === `sexy-proposed-${pdfTemplate.pdfTemplateUrl}` ? (
                <CircularProgress size={24} color={"primary"} />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={getHandleClick({
              templateType: "sexy",
              calculationType: "proposed",
              pdfTemplateUrl: pdfTemplate.pdfTemplateUrl,
            })}
            disabled={
              loadingPdf === `sexy-proposed-${pdfTemplate.pdfTemplateUrl}`
            }
            sx={{ whiteSpace: "normal" }}
          >
            {pdfTemplate.label}
          </MenuItemWithIcon>
        )),
      ]}
      {hasPdgGeneratorTemplates && [
        <Divider key="divider-generator" />,
        <ListSubheader
          key="header-generator"
          sx={{ color: "grey.500", minHeight: "36px", lineHeight: "36px" }}
        >
          {t("Generator PDF", { ns: "Quote" })}
        </ListSubheader>,
        ...pdfGeneratorTemplates.flatMap(template =>
          [
            hasSelectedCalculations && (
              <MenuItemWithIcon
                key={`${template.id}-agreed-generator`}
                icon={
                  loadingPdf === `generator-agreed-${template.id}` ? (
                    <CircularProgress size="1.5rem" color={"primary"} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={getHandleClick({
                  templateType: "generator",
                  calculationType: "agreed",
                  pdfGeneratorTemplateId: template.id,
                })}
                disabled={loadingPdf === `generator-agreed-${template.id}`}
                sx={{ whiteSpace: "normal" }}
              >
                {`${template.name} (${t("agreed", { ns: "Quote" })})`}
              </MenuItemWithIcon>
            ),
            <MenuItemWithIcon
              key={`${template.id}-proposed-generator`}
              icon={
                loadingPdf === `generator-proposed-${template.id}` ? (
                  <CircularProgress size={24} color={"primary"} />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={getHandleClick({
                templateType: "generator",
                calculationType: "proposed",
                pdfGeneratorTemplateId: template.id,
              })}
              disabled={loadingPdf === `generator-proposed-${template.id}`}
              sx={{ whiteSpace: "normal" }}
            >
              {hasSelectedCalculations
                ? `${template.name} (${t("proposed", { ns: "Quote" })})`
                : template.name}
            </MenuItemWithIcon>,
          ].filter(value => value)
        ),
        debug && <Divider key="divider-inspect" />,
        debug && hasSelectedCalculations && (
          <MenuItemWithIcon
            key="inspect-agreed"
            icon={
              loadingQuoteData === "inspect-agreed" ? (
                <CircularProgress size="1.5rem" color={"primary"} />
              ) : (
                <DataObjectIcon />
              )
            }
            onClick={getHandleInspect("agreed")}
            disabled={loadingQuoteData === "inspect-agreed"}
            sx={{ whiteSpace: "normal" }}
          >
            {`${t("Inspect quote data", { ns: "Quote" })} (${t("agreed", { ns: "Quote" })})`}
          </MenuItemWithIcon>
        ),
        debug && (
          <MenuItemWithIcon
            key="inspect-proposed"
            icon={
              loadingQuoteData === "inspect-proposed" ? (
                <CircularProgress size={24} color={"primary"} />
              ) : (
                <DataObjectIcon />
              )
            }
            onClick={getHandleInspect("proposed")}
            disabled={loadingQuoteData === "inspect-proposed"}
            sx={{ whiteSpace: "normal" }}
          >
            {hasSelectedCalculations
              ? `${t("Inspect quote data", { ns: "Quote" })} (${t("proposed", { ns: "Quote" })})`
              : t("Inspect quote data", { ns: "Quote" })}
          </MenuItemWithIcon>
        ),
      ]}
      {inspectorData !== null && (
        <QuoteDataInspectorModal
          quoteData={inspectorData}
          handleClose={() => setInspectorData(null)}
        />
      )}
      {loading && (
        <MenuItemWithIcon
          icon={<CircularProgress size={20} color={"primary"} />}
          disabled={true}
        >
          {t("Loading…", { ns: "Global" })}
        </MenuItemWithIcon>
      )}
    </>
  );
}
